import React from "react";
import { useHistory, useLocation } from "react-router-dom";
import Dropdown from "core/ui/Dropdown";
import { useState, useEffect, useContext } from "react";
import SessionContext from "SessionContext";
import ChannelDetails from "./royalty-channel-details";
import ConfirmSuccess from "../core/ui/confirm-success";
import isEqual from "lodash.isequal";
import UnsavedChanges from "partials/unsaved-changes";
import { Alert } from "reactstrap";

const selectionConstructor = { id: 0, name: "" };

function RoyaltyEditForm(props) {
  const history = useHistory();
  const session = useContext(SessionContext);
  const location = useLocation();

  const [source, setSource] = useState(selectionConstructor);
  const [product, setProduct] = useState(selectionConstructor);
  const [subProduct, setSubProduct] = useState(selectionConstructor);
  const [channel, setChannel] = useState({});
  const [editingId, setEditingId] = useState(null);
  const [billingCodes, setBillingCodes] = useState([]);
  const [originalData, setOriginalData] = useState(null);
  const [qbDescription, setQbDescription] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [sourceOptionsList, setSourceOptionsList] = useState(null);
  const [productOptionsList, setProductOptionsList] = useState(null);
  const [subProductOptionsList, setSubProductOptionsList] = useState(null);
  const [billingCodesOptionsList, setBillingCodesOptionsList] = useState(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [modified, setModified] = useState(false);
  const [channelHasChanges, setChannelHasChanges] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isClone, setIsClone] = useState(false);

  useEffect(() => {
    loadSourceList();
    loadProductList();
    loadSubProductList();
    loadBillingCodeList();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (props.args.royalty_id) {
      loadRoyaltyCostDetails(props.args.royalty_id);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Get all the details to preload the fields
   */
  function loadRoyaltyCostDetails(royalty_id) {
    setIsLoading(true);
    const searchParams = new URLSearchParams(location.hash.replace("#", "?"));
    const _is_clone = searchParams.get("clone") === "true";
    session
      .query("admin/get_royalty_cost", { id: royalty_id })
      .then((response) => {
        setSource(response.royalty_source);
        setProduct(response.royalty_product);
        setSubProduct(response.royalty_subproduct);
        setChannel({
          channel_option_id: response.channel_option.id,
          effective_date: _is_clone ? "" : response.effective_date.slice(0, 10),
          cost_per_record: response.cost_per_record,
        });
        setEditingId(royalty_id);
        setBillingCodes(response.billing_codes);
        setQbDescription(response.qb_description || "");
        setOriginalData(_is_clone ? {...response, effective_date: ""} : response);
        setIsClone(_is_clone);
        setLoadingOff();
      })
      .catch((err) => {
        console.log("Failed to load the royalty details.", err)
        setLoadingOff();
      })
  }

  function loadSourceList() {
    session
      .query("admin/get_royalty_sources")
      .then((response) => {
        setSourceOptionsList(response);
      })
      .catch((error) => {
        console.log(JSON.stringify(error));
      });
  }

  function loadProductList() {
    session
      .query("admin/get_royalty_products")
      .then((response) => {
        setProductOptionsList(response);
      })
      .catch((error) => {
        console.log(JSON.stringify(error));
      });
  }

  function loadSubProductList() {
    session
      .query("admin/get_royalty_subproducts")
      .then((response) => {
        setSubProductOptionsList(response);
      })
      .catch((error) => {
        console.log(JSON.stringify(error));
      });
  }

  function loadBillingCodeList() {
    session
      .query("admin/get_billing_codes")
      .then((response) => {
        setBillingCodesOptionsList(response);
      })
  }

  const goBack = () => {
    history.push("/admin-royalty-pricing");
  };

  /**
   * Handle changes made to the original
   */
  const handleSelectedSource = (selectedSource) => {
    if (selectedSource !== null) {
      setSource(selectedSource);
      checkIfChanged();
    }
  };

  const handleSelectedProduct = (selectedProduct) => {
    if (selectedProduct !== null) {
      setProduct(selectedProduct);
      checkIfChanged();
    }
  };

  const handleSelectedSubProduct = (selectedSubProduct) => {
    if (selectedSubProduct !== null) {
      setSubProduct(selectedSubProduct);
      checkIfChanged();
    }
  };

  const handleSelectedChannel = (selectedChannel) => {
    setChannel(selectedChannel);
  };

  const handleQbDescription = (ev) => {
    ev.preventDefault();
    setQbDescription(ev.target.value);
  }

  const handleSelectedBillingCode = (selectedBillingCode) => {
    let newCodes = [...billingCodes];
    let isDuplicate = [];
    newCodes.forEach((code) => {
      if (code.id === selectedBillingCode.id) {isDuplicate.push(code)};
    });
    // don't add a duplicate option
    if (isDuplicate.length < 1) {
      newCodes.push(selectedBillingCode);
      setBillingCodes(newCodes);
    }
  };

  const handleDeleteBillingCode = (deleteCodeId) => {
    let newCodes = billingCodes.filter((code) => {
      return code.id !== deleteCodeId;
    });
    setBillingCodes(newCodes);
  };

  const compareValues = () => {
    if(
      source.id !== originalData.royalty_source.id ||
      product.id !== originalData.royalty_product.id ||
      subProduct.id !== originalData.royalty_subproduct.id ||
      qbDescription !== (originalData.qb_description || "") ||
      billingCodes !== originalData.billing_codes ||
      channel.cost_per_record !== originalData.cost_per_record
    ) {
      return true;
    } else {
      return false;
    }
  }

  const checkIfChanged = () => {
    if(originalData !== null) {
      // format the local state to match the api response to compare
      let _selectedChannel = {
        channel_option_id: originalData.channel_option.id || 0,
        effective_date: originalData.effective_date.split("T")[0],
        cost_per_record: originalData.cost_per_record
      }

      // check the date is filled in
      if(!channel.effective_date.length) {
        return false;
      }

      // check if is a clone: needs to have changes
      if(isClone && !compareValues()) {
        return false;
      }

      // check if channel has changed
      if(channelHasChanges || !isEqual(channel, _selectedChannel)) {
        return true;
      }

      if( compareValues() ) {
        return true;
      } else {
        return false;
      }
    } else {
      return true;
    }
  };

  /**
   * Prepare and send updated data to api
   */
  const handleSave = () => {
    let prettyChannelFormat = {
      channel_option_id: channel.channel_option_id,
      effective_date: channel.effective_date.replace(/-/g, ""),
      cost_per_record: Number(channel.cost_per_record),
    };
    let finalBillingCodes = [];
    billingCodes.forEach((code) => {
      finalBillingCodes.push(code.id);
    });

    const createNew = {
      id: isClone ? null : Number(editingId),
      royalty_source_id: source.id,
      royalty_product_id: product.id,
      royalty_subproduct_id: subProduct.id,
      channel_options: [prettyChannelFormat],
      billing_code_ids: finalBillingCodes,
      qb_description: qbDescription
    };

    let _url = isClone ? `admin/create_royalty_cost` : `admin/update_royalty_cost`;

    session
      .command(
        _url,
        createNew,
        {},
        { defaultErrorHandling: false }
      )
      .then(() => {
        setShowSuccess(true);
        setHasUnsavedChanges(false);
        setTimeout(() => {
          setModified(true);
          goBack();
        }, 2000);
      })
      .catch((error) => {
        console.log("Royalty Cost Update Failed", JSON.stringify(error));
      });
  };

  const checkAllValues = () => {
    if(
      source.name === "" ||
      product.name === "" ||
      subProduct.name === "" ||
      billingCodes.length < 1 
    ) {
      return true;
    } else {
      return false;
    }
  };

  const handleCloneClick = () => {
    let _id = props.args.royalty_id;
    /* reload page in clone mode */
    window.location.href = `${window.location.origin}/admin-royalty-edit-form#royalty_id=${_id}&clone=true`;
    window.location.reload();
  }

  useEffect(() => {
    // check if data has finished loading
    if( originalData && source.id && product.id && subProduct.id && billingCodes.length && Object.keys(channel).length ) {
      setTimeout(() => {
        setIsLoading(false);
      }, 1500);
    } else {
      setIsLoading(true);
    }
    
    let _hasChanged = false;
    if(checkIfChanged()) {
      _hasChanged = true;
    }
    if(checkAllValues()) {
      _hasChanged = true;
    }
    setModified(!_hasChanged);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [originalData, source, product, subProduct, billingCodes, qbDescription, channel, channelHasChanges])

  useEffect(() => {
    if(!modified || channelHasChanges) {
      setHasUnsavedChanges(true);
    } else {
      setHasUnsavedChanges(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modified, channelHasChanges])

  const setLoadingOff = () => {
    setTimeout(() => {
      setIsLoading(false);
    }, 1500);
  }

  return (
    <div className="page royalty-form-page">
      <div className="list-header sticky-page-header">
        <button type="button" className="new-button" onClick={goBack}>
          <i className="bi bi-chevron-left" />{" "} Back
        </button>
        <h1>Edit Royalty Pricing</h1>
        <i className="spacer" />
      </div>

      {isLoading && <div className="royalty-loading-overlay">Loading...</div>}

      <div className="main-section">
        <div className="left-selectors">
          <span className="label">Source</span>
          <Dropdown
            label="Select Source"
            options={sourceOptionsList}
            select={handleSelectedSource}
            remove={false}
            disable={false}
            preSelect={source}
          />
          <span className="label">Product</span>
          <Dropdown
            label="Select Product"
            options={productOptionsList}
            select={handleSelectedProduct}
            remove={false}
            disable={false}
            preSelect={product}
          />
          <span className="label">Sub Product</span>
          <Dropdown
            label="Select Sub Product"
            options={subProductOptionsList}
            select={handleSelectedSubProduct}
            remove={false}
            disable={false}
            preSelect={subProduct}
          />
          <span className="label">QB Description (Optional)</span>
          <textarea 
            className="text-input" 
            value={qbDescription}
            onChange={handleQbDescription}
          />
          <span className="label">Billing Code</span>
          <Dropdown
            label="Add Billing Code"
            options={billingCodesOptionsList}
            select={handleSelectedBillingCode}
            disable={false}
            remove={false}
            fieldToDisplay="description"
            fixedLabel
          />

          <div className="submit-cancel">
            <button
              type="button"
              className="btn btn-light btn-royalty"
              onClick={goBack}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn-royalty promote"
              onClick={handleSave}
              disabled={modified}
            >
              {isClone ? "Create" : "Update"}
            </button>   
          </div>

          <div className="clone-section">
            {!isClone ? (<>
            <button
              type="button"
              className="btn btn-light btn-royalty btn-clone"
              onClick={handleCloneClick}
            >
              Clone
            </button>
          </>) : (<>
            <Alert>
              <i className="bi bi-lightbulb"></i>
              <b>Note:</b> You are creating a clone of an existing Royalty Cost.
              <br /><br />
              Before saving, make sure to:
              <ul className="mb-1">
                <li>Update the necessary fields to make it distinct from the original.</li>
                <li className="mt-2">Enter a new effective date.</li>
              </ul>
            </Alert>
            <Alert color="warning" isOpen={!channel.effective_date}>
              <i className="bi bi-exclamation-diamond"></i>
              Effective Date required {channel.effective_date}
            </Alert>
            <Alert color="warning" isOpen={modified}>
              <i className="bi bi-exclamation-diamond"></i>
              Changes to the existing royalty cost record required
            </Alert>
          </>)}
          </div> 
        </div>
        
        <div className="right-summary">
          <h5>Summary</h5>
          <div>
            Source: <strong>{source.name}</strong>
          </div>
          <div>
            Product: <strong>{product.name}</strong>
          </div>
          <div>
            Sub Product: <strong>{subProduct.name}</strong>
          </div>
          <div>
            Billing Codes:
            <div className="billing-codes">
              {billingCodes.length > 0 &&
                billingCodes.map((bCode) => {
                  return (
                    <span className="selected-code" key={bCode.id}>
                      {bCode.description}
                      <button
                        type="button"
                        className="remove-billing-code"
                        onClick={() => handleDeleteBillingCode(bCode.id)}
                      >
                        <i className="bi bi-x-circle"></i>
                      </button>
                    </span>
                  );
                })}
            </div>
          </div>

          <ChannelDetails
            select={handleSelectedChannel}
            key={channel.indexValue}
            preSetChannel={{ id: channel.channel_option_id }}
            preSetPrice={channel.cost_per_record}
            preSetDate={channel.effective_date}
            channelHasChanged={(val) => setChannelHasChanges(val)}
          />
        </div>
      </div>
      <ConfirmSuccess show={showSuccess} label={isClone ? "Saved!" : "Updated!"} />
      <UnsavedChanges hasUnsavedChanges={hasUnsavedChanges} />
    </div>
  );
}

export default RoyaltyEditForm;
